/**
 * MentionTextarea — shared @user / #dept / /entity autocomplete textarea.
 *
 * Drop-in replacement for <textarea>. Used by:
 *   • Department Chat composer
 *   • Call Relay modal
 *   • Critical-Value Callback notes
 *
 * Triggers (matching the chat composer):
 *   @<q>      → user search       /api/chat/users/search
 *   #<q>      → channel/dept      (in-memory match against channels)
 *   /r<q>     → requisition       /api/chat/attach-search?type=requisition
 *   /res<q>   → result
 *   /o<q>     → order
 *   /s<q>     → specimen
 *   /p<q>     → patient
 *   /c<q>     → client
 *   /i<q>     → insurance
 *
 * Picks accumulate into the parent's `mentions` state (users, depts,
 * entities). The parent ships those alongside the message body so the
 * server can resolve them to records and send notifications.
 *
 * Channels:
 *   - Pass `channels` if the host page already has them.
 *   - Otherwise the component fetches /api/chat/channels on first render.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';

const SLASH_PREFIXES = [
  // Order matters: longer prefixes first so /email beats /e and /res beats /r.
  { prefix: '/email', type: 'email' },
  { prefix: '/res',   type: 'result' },
  { prefix: '/f',     type: 'fax' },
  { prefix: '/r',     type: 'requisition' },
  { prefix: '/o',     type: 'order' },
  { prefix: '/s',     type: 'specimen' },
  { prefix: '/p',     type: 'patient' },
  { prefix: '/c',     type: 'client' },
  { prefix: '/i',     type: 'insurance' },
];

export default function MentionTextarea({
  value,
  onChange,
  channels: channelsProp,
  mentions,
  setMentions,
  placeholder,
  style,
  disabled,
  onKeyDown,
}) {
  const inputRef = useRef(null);
  const [mq, setMq]           = useState(null);   // {trigger, query, start, end, entityType?}
  const [results, setResults] = useState([]);
  const [idx, setIdx]         = useState(0);
  const [channels, setChannels] = useState(channelsProp || []);

  // Self-fetch channels when host didn't pass any.
  useEffect(() => {
    if (channelsProp && channelsProp.length) { setChannels(channelsProp); return; }
    let cancelled = false;
    api.get('/api/chat/channels')
      .then(r => { if (!cancelled) setChannels(r.data?.channels || []); })
      .catch(() => { if (!cancelled) setChannels([]); });
    return () => { cancelled = true; };
  }, [channelsProp]);

  const detect = (val, cur) => {
    if (cur == null) return null;
    const before = val.slice(0, cur);
    const m = before.match(/(?:^|\s)([@#])([A-Za-z0-9_.\-]*)$/);
    if (m) {
      const trigger = m[1];
      const queryStr = m[2];
      const start = cur - queryStr.length - 1;
      return { trigger, query: queryStr, start, end: cur };
    }
    for (const { prefix, type } of SLASH_PREFIXES) {
      const rx = new RegExp(`(?:^|\\s)${prefix.replace('/', '\\/')}([A-Za-z0-9_.\\-]*)$`);
      const sm = before.match(rx);
      if (sm) {
        if (prefix === '/r' && /^(es|ai)/i.test(sm[1])) continue;
        const queryStr = sm[1];
        const start = cur - queryStr.length - prefix.length;
        return { trigger: prefix, entityType: type, query: queryStr, start, end: cur };
      }
    }
    return null;
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setMq(detect(v, e.target.selectionStart));
    setIdx(0);
  };

  // Debounced fetch for matches.
  useEffect(() => {
    if (!mq) { setResults([]); return; }
    const t = setTimeout(async () => {
      const q = (mq.query || '').toLowerCase();
      if (mq.trigger === '#') {
        const matches = (channels || [])
          .filter(c => !q || c.key.toLowerCase().startsWith(q) || c.label.toLowerCase().includes(q))
          .slice(0, 12)
          .map(c => ({
            _kind: 'dept',
            id: `dept-${c.key}`,
            username: c.key,
            full_name: c.label,
            label: c.label,
            icon: c.icon,
            color: c.color,
          }));
        setResults(matches);
      } else if (mq.entityType) {
        try {
          const r = await api.get('/api/chat/attach-search', {
            params: { type: mq.entityType, q: mq.query || '', limit: 8 },
          });
          setResults((Array.isArray(r.data) ? r.data : []).map(it => ({
            ...it, _kind: 'entity', entityType: mq.entityType, trigger: mq.trigger,
          })));
        } catch { setResults([]); }
      } else {
        try {
          const r = await api.get('/api/chat/users/search', { params: { q: mq.query || ' ', limit: 8 } });
          setResults((Array.isArray(r.data) ? r.data : []).map(u => ({ ...u, _kind: 'user' })));
        } catch { setResults([]); }
      }
    }, 120);
    return () => clearTimeout(t);
  }, [mq, channels]);

  const insert = (item) => {
    if (!mq) return;
    const before = value.slice(0, mq.start);
    const after  = value.slice(mq.end);
    let insertion;
    if (item._kind === 'dept') {
      insertion = `#${item.label} `;
      setMentions(prev => prev.depts.includes(item.username)
        ? prev
        : { ...prev, depts: [...prev.depts, item.username] });
    } else if (item._kind === 'entity') {
      insertion = `${item.label} `;
      setMentions(prev => {
        const k = `${item.entityType}:${item.id}`;
        if (prev.entities.find(p => `${p.type}:${p.id}` === k)) return prev;
        return { ...prev, entities: [...prev.entities, { type: item.entityType, id: String(item.id), label: item.label }] };
      });
    } else {
      const display = item.full_name || item.username;
      insertion = `@${display} `;
      setMentions(prev => prev.users.includes(item.username)
        ? prev
        : { ...prev, users: [...prev.users, item.username] });
    }
    const newVal = before + insertion + after;
    onChange(newVal);
    setMq(null); setResults([]);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const pos = before.length + insertion.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const onKey = (e) => {
    if (mq && results.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => (i + 1) % results.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => (i - 1 + results.length) % results.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insert(results[idx]); return; }
      if (e.key === 'Escape')    { setMq(null); setResults([]); return; }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const removeTag = (kind, key) => {
    setMentions(prev => {
      if (kind === 'user')   return { ...prev, users:    prev.users.filter(u => u !== key) };
      if (kind === 'dept')   return { ...prev, depts:    prev.depts.filter(d => d !== key) };
      if (kind === 'entity') return { ...prev, entities: prev.entities.filter(e => `${e.type}:${e.id}` !== key) };
      return prev;
    });
  };

  const total = (mentions?.users?.length || 0) + (mentions?.depts?.length || 0) + (mentions?.entities?.length || 0);

  return (
    <div style={{ position: 'relative' }}>
      <textarea ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKey}
        disabled={disabled}
        placeholder={placeholder || 'Type @ to mention a user, # for a department…'}
        style={style} />
      {mq && results.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0,
          background: 'var(--card-bg, #fff)',
          border: '1px solid var(--divider, #e5e7eb)',
          borderRadius: 8,
          marginBottom: 4, maxHeight: 220, overflowY: 'auto', zIndex: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}>
          {results.map((r, i) => {
            const isActive = i === idx;
            const sub =
              r._kind === 'dept'   ? `#${r.username}` :
              r._kind === 'entity' ? (r.entityType || 'ITEM').toUpperCase() :
                                     `@${r.username || ''}`;
            // Compose a stable key — id alone collides for fax/email
            // results that share a value (e.g. two clients with the same
            // fax number), so include a per-result index as a tiebreaker.
            return (
              <div key={`${r._kind}-${r.id}-${i}`} onClick={() => insert(r)}
                onMouseEnter={() => setIdx(i)}
                style={{
                  padding: '8px 12px', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: 10, fontSize: 13,
                  background: isActive ? 'rgba(59,130,246,0.13)' : 'transparent',
                }}>
                {r._kind === 'dept' && <span style={{ fontSize: 14 }}>{r.icon}</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary, #111827)' }}>
                    {r.full_name || r.label || r.username}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {mentions.users.map(u => (
            <Chip key={`u-${u}`} color="#3b82f6" onRemove={() => removeTag('user', u)}>@{u}</Chip>
          ))}
          {mentions.depts.map(d => (
            <Chip key={`d-${d}`} color="#8b5cf6" onRemove={() => removeTag('dept', d)}>#{d}</Chip>
          ))}
          {mentions.entities.map(e => (
            <Chip key={`e-${e.type}-${e.id}`} color="#0891b2" onRemove={() => removeTag('entity', `${e.type}:${e.id}`)}>
              {e.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ children, color, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: `${color}1a`, color, border: `1px solid ${color}33`,
    }}>
      {children}
      {onRemove && <X size={11} style={{ cursor: 'pointer' }} onClick={onRemove} />}
    </span>
  );
}
